import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { IInHomeCareLicense } from 'app/models/licenses/in-home-care/in-home-care-license';
import { ISearch } from 'app/models/licenses/search';
import { IStaff } from 'app/models/user/staff';
import { ILicenseRequest } from 'app/models/licenses/license-request';

@Injectable({providedIn: 'root'})
export class InHomeCareLicenseService {
  private api: string = environment.privateApi + 'InHomeCareLicense/';
  private changeApi: string = environment.privateApi + 'InHomeCareChange/';
  private reportApi: string = environment.privateApi + 'InHomeCareReport/';
  private dataMigrationApi: string = environment.privateApi + 'DataMigration/';

  public limited: string = 'Limited';
  public basic: string = 'Basic';
  public intermediate: string = 'Intermediate';
  public comprehensive: string = 'Comprehensive';

  private http = inject(HttpClient);

  public searchLicenses(search: ISearch): Observable<IInHomeCareLicense[]> {
    return this.http.post<IInHomeCareLicense[]>(this.api + 'SearchLicenses', search);
  }

  public getAssignedLicenses(): Observable<IInHomeCareLicense[]> {
    return this.http.get<IInHomeCareLicense[]>(this.api + 'GetAssignedLicenses');
  }

  public approveLicense(licenseId: number, approved: boolean): Observable<IInHomeCareLicense>{
    return this.http.post<IInHomeCareLicense>(this.api + 'ApproveLicense/' + licenseId + '/' + approved, null);
  }

  public assignLicense(licenseId: number, staff: IStaff): Observable<boolean>{
    return this.http.post<boolean>(this.api + 'AssignLicense/' + licenseId, staff);
  }

  public createLicense(): Observable<IInHomeCareLicense> {
    return this.http.post<IInHomeCareLicense>(this.api + 'CreateLicense', null);
  }

  public getLicenses(): Observable<IInHomeCareLicense[]> {
    return this.http.get<IInHomeCareLicense[]>(this.api + 'GetLicenses');
  }

  public getParentInfo(entityName: string): Observable<IInHomeCareLicense> {
    return this.http.get<IInHomeCareLicense>(this.api + 'GetParentInfo/' + entityName);
  }

  public checkIfNameValid(entityName: string, licenseId: string): Observable<boolean> {
    return this.http.get<boolean>(this.api + 'CheckIfNameValid/' + encodeURIComponent(entityName), { params: { licenseId } });
  }

  public checkIfValidParent(entityName: string): Observable<boolean>
  {
    return this.http.get<boolean>(this.api + 'CheckIfValidParent/' + entityName);
  }

  public updateLicense(license: IInHomeCareLicense): Observable<boolean>{
    return this.http.post<boolean>(this.api + 'UpdateLicense', license);
  }

  public archiveLicense(licenseId: number): Observable<boolean>{
    return this.http.post<boolean>(this.api + 'ArchiveLicense/' + licenseId, null);
  }

  public updateLicenseStatus(licenseId: number, statusId: number): Observable<IInHomeCareLicense>{
    return this.http.post<IInHomeCareLicense>(this.api + 'UpdateLicenseStatus/' + licenseId + '/' + statusId, null);
  }

  public submitLicense(license: IInHomeCareLicense): Observable<boolean>{
    return this.http.post<boolean>(this.api + 'SubmitLicense', license);
  }

  public getStaff(licenseType: string): Observable<IStaff[]> {
    return this.http.get<IStaff[]>(this.api + 'GetStaff/' + licenseType);
  }

  public downloadReport(report: string): Observable<Blob>{
    return this.http.get<Blob>(this.reportApi + report, { responseType: 'blob' as 'json' })
  }

  public uploadIHCData(file: FormData): Observable<boolean>{
      return this.http.post<boolean>(this.dataMigrationApi + 'ImportIHCData', file)
  }

  public uploadBranchData(file: FormData): Observable<boolean>{
    return this.http.post<boolean>(this.dataMigrationApi + 'ImportIHCBranchData', file)
  }

  public getLicenseRequests(ownerChange: boolean, includeAll: boolean): Observable<ILicenseRequest[]>{
    return this.http.get<ILicenseRequest[]>(this.api + 'GetLicenseRequests/' + ownerChange + '/' + includeAll);
  }

  public checkLicenseRequest(licenseId: string): Observable<string> {
    return this.http.get(this.api + 'CheckLicenseRequest/' + licenseId, { responseType: 'text' });
  }

  public approveLicenseRequest(licenseRequest: ILicenseRequest): Observable<boolean>{
    return this.http.post<boolean>(this.changeApi + 'ApproveLicenseRequest', licenseRequest);
  }
}
